import {
  isDarkTheme,
  Slider,
  toggleTheme,
  useThemeContext,
} from "@hoylu/client-common";
import { clsx } from "clsx";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import {
  enableWorkspaceExport as selectEnableWorkspaceExport,
  managementPortal as selectManagementPortal,
  showCreditLimitUi as selectShowCreditLimitUi,
} from "../state/config/config.selector";
import * as userActions from "../state/user/user.actions";
import {
  selectUserMarbleColor,
  selectUserProfile,
  selectUserToken,
} from "../state/user/user.selector";
import * as workspacesActions from "../state/workspaces/workspaces.actions";
import {
  selectEffectiveTonnageUsage,
  selectIsCreditLimitReached,
  selectIsUserLicensed,
  selectUserLicenseType,
} from "../state/workspaces/workspaces.selector";
import { useI18n } from "../utils/hooks/use.i18n";
import { managementPortalBillingUrl } from "../utils/management.portal/management.portal.url.selectors";
import { openManagementPortal } from "../utils/management.portal/openManagementPortal";
import { isOrganizationAdmin } from "../utils/sharing.helpers";
import { CircularProgressBar } from "./CircularProgressBar";
import UserProfileMarble from "./UserProfileMarble";
import Styles from "./UserProfileMenu.module.css";

const selectIsOrgAdmin = createSelector([selectUserToken], isOrganizationAdmin);

const MenuIcon = ({ icon }: { icon: string }) => (
  <div className={clsx(icon, Styles.icon)} role="none" />
);

const MenuLabel = ({ children }: React.PropsWithChildren) => (
  <div className={Styles.optionText}>{children}</div>
);

const UserProfileMenu = () => {
  const dispatch = useDispatch();

  const t = useI18n("USER_PROFILE_WIDGET.");

  const themeContext = useThemeContext();

  const billingUrl = useSelector(managementPortalBillingUrl);
  const userColor = useSelector(selectUserMarbleColor);
  const effectiveTonnageUsage = useSelector(selectEffectiveTonnageUsage);
  const isCreditLimitReached = useSelector(selectIsCreditLimitReached);
  const isOrgAdmin = useSelector(selectIsOrgAdmin);
  const isUserLicensed = useSelector(selectIsUserLicensed);
  const isWorkspaceExportEnabled = useSelector(selectEnableWorkspaceExport);
  const licenseType = useSelector(selectUserLicenseType);
  const managementPortalUrl = useSelector(selectManagementPortal);
  const profile = useSelector(selectUserProfile);
  const showCreditLimitUi = useSelector(selectShowCreditLimitUi);

  const userEmail = profile.email;

  const usageNote = isCreditLimitReached
    ? t("USAGE_OVER_LIMIT")
    : t("USAGE_UNDER_LIMIT", effectiveTonnageUsage);

  const isDarkThemeSelected = isDarkTheme(themeContext.theme);

  const showUpload = () => {
    dispatch(
      workspacesActions.activateDashboardOption({
        optionType: DashboardOption.UPLOAD,
      }),
    );
  };

  const invite = () =>
    dispatch(
      workspacesActions.activateDashboardOption({
        optionType: DashboardOption.INVITE,
      }),
    );

  return (
    <div className={Styles.userProfileMenu}>
      <div className={Styles.userProfileMenuContent}>
        {showCreditLimitUi && (
          <div className={Styles.marbleContainer}>
            <CircularProgressBar
              percentage={effectiveTonnageUsage}
              themeColor={userColor}
            >
              {isUserLicensed ? (
                <UserProfileMarble profile={profile} userColor={userColor} />
              ) : (
                <div className={Styles.tonnageUsage}>
                  {effectiveTonnageUsage}
                  <span>%</span>
                </div>
              )}
            </CircularProgressBar>
            {isUserLicensed ? (
              <div className={Styles.license}>
                <span className={Styles.userName}>
                  {profile.firstName} {profile.lastName}
                </span>
                <span className={Styles.licenseType}>
                  {t("PLAN_TITLE", licenseType)}
                </span>
              </div>
            ) : (
              <span className={Styles.noLicense}>{usageNote}</span>
            )}
          </div>
        )}
        <ul className={Styles.listContainer}>
          <li className={Styles.email} title={userEmail}>
            {userEmail}
          </li>
          {!isUserLicensed && showCreditLimitUi && (
            <li
              className={clsx(
                Styles.option,
                isCreditLimitReached && Styles.creditLimitReached,
              )}
              data-test-id="user-menu-upgrade"
              onClick={() => openManagementPortal(billingUrl)}
              title={t("UPGRADE")}
            >
              <MenuIcon icon="hoylu-ui-icons-star-filled" />
              <MenuLabel>{t("UPGRADE")}</MenuLabel>
            </li>
          )}
          <li
            className={Styles.option}
            onClick={() => {
              dispatch(
                workspacesActions.activateDashboardOption({
                  optionType: DashboardOption.MANAGE_ACCOUNT,
                }),
              );
            }}
            title={t("MANAGE_ACCOUNT")}
          >
            <MenuIcon icon="hoylu-ui-icons-head" />
            <MenuLabel>{t("MANAGE_ACCOUNT")}</MenuLabel>
          </li>
          {isOrgAdmin && !!managementPortalUrl && (
            <li
              className={Styles.option}
              onClick={() => {
                window.open(encodeURI(managementPortalUrl), "_newtab");
              }}
              title={t("MANAGEMENT_PORTAL")}
            >
              <MenuIcon icon="hoylu-ui-icons-construction2" />
              <MenuLabel>{t("MANAGEMENT_PORTAL")}</MenuLabel>
            </li>
          )}
          {isWorkspaceExportEnabled && (
            <li
              className={Styles.option}
              onClick={showUpload}
              title={t("UPLOAD")}
            >
              <MenuIcon icon="hoylu-ui-icons-upload" />
              <MenuLabel>{t("UPLOAD")}</MenuLabel>
            </li>
          )}
          <li
            className={Styles.option}
            onClick={invite}
            title={t("INVITE_USER")}
          >
            <MenuIcon icon="hoylu-ui-icons-send" />
            <MenuLabel>{t("INVITE_USER")}</MenuLabel>
          </li>
          <li className={Styles.theme}>
            <div
              className={isDarkThemeSelected ? Styles.inactive : Styles.active}
            >
              {t("THEME_LIGHT_TITLE")}
            </div>
            <Slider
              checked={isDarkThemeSelected}
              color="var(--global--notification--Color)"
              onChange={() => toggleTheme(themeContext)}
              title={t("SWITCH_COLOR_SCHEME")}
              useCheckIndication={false}
            />
            <div
              className={isDarkThemeSelected ? Styles.active : Styles.inactive}
            >
              {t("THEME_DARK_TITLE")}
            </div>
          </li>
          <li
            className={Styles.option}
            onClick={() => dispatch(userActions.logout.request())}
            title={t("LOGOUT")}
          >
            <MenuIcon icon="hoylu-ui-icons-export" />
            <MenuLabel>{t("LOGOUT")}</MenuLabel>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default UserProfileMenu;
